import React, { useState } from "react";
import { ROUTINES, getRoutinesByCategory } from "../routines";

const Z = {
  primary:             "#55624d",
  primaryFixed:        "#d9e7cd",
  primaryFixedDim:     "#bdcbb2",
  onPrimary:           "#ffffff",
  surface:             "#f8faf3",
  surfaceContainer:    "#ecefe8",
  surfaceContainerLow: "#f2f4ed",
  surfaceContainerLowest: "#ffffff",
  onSurface:           "#191c18",
  onSurfaceVariant:    "#444841",
  secondary:           "#755754",
  secondaryContainer:  "#fed7d2",
  onSecondaryContainer:"#795b58",
  outline:             "#757870",
  outlineVariant:      "#c5c8be",
  inverseSurface:      "#2e312d",
  inverseOnSurface:    "#eff2ea",
};

// ─────────────────────────────────────────────
// ZIELE
// ─────────────────────────────────────────────
const GOALS = [
  { id: "custom",      icon: "✏️", label: "Eigene Routine",  desc: "Starte ohne Vorgaben — füge nur die Habits hinzu die zu dir passen", routines: ["custom"] },
  { id: "health",      icon: "🧬", label: "Gesundheit",    desc: "Körper stärken, Energie steigern",   routines: ["neuro", "scientist", "athlete"] },
  { id: "performance", icon: "⚡", label: "Performance",   desc: "Fokus, Produktivität und mentale Stärke maximieren", routines: ["operator", "grinder", "warrior"] },
  { id: "mindset",     icon: "🧠", label: "Mindset",       desc: "Innere Ruhe, Disziplin und Selbstkenntnis aufbauen",  routines: ["monk", "activator", "scientist"] },
  { id: "longevity",   icon: "🫀", label: "Longevity",     desc: "Das Maximum aus deinen Jahren herausholen",          routines: ["legend", "scientist", "explorer"] },
  { id: "body",        icon: "💪", label: "Körper & Physique", desc: "Aufbau, Ästhetik und Regeneration",               routines: ["sculptor", "athlete", "athlete_protocol"] },
];

// ─────────────────────────────────────────────
// PROGRESS DOT
// ─────────────────────────────────────────────
function ProgressDots({ total, current }) {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === current ? 20 : 6, height: 6, borderRadius: 3,
          background: i <= current ? Z.primary : Z.outlineVariant,
          transition: "all 0.3s ease",
        }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// SCREEN 1 — ZIEL WÄHLEN
// ─────────────────────────────────────────────
function GoalScreen({ onNext }) {
  const [selected, setSelected] = useState(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 20px" }}>
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 32, fontWeight: 300, color: Z.primary, letterSpacing: "-0.03em", lineHeight: 1.2, margin: "0 0 10px" }}>
            Was ist dein<br />wichtigstes Ziel?
          </h1>
          <p style={{ fontSize: 14, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.6 }}>
            Wähle einen Fokus — du kannst ihn später jederzeit ändern.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 20 }}>
          {GOALS.map((g) => (
            <button
              key={g.id}
              onClick={() => setSelected(g.id)}
              style={{
                textAlign: "left", padding: "16px 18px", borderRadius: 18,
                border: `${selected === g.id ? "2px" : "1px"} solid ${selected === g.id ? Z.primary : Z.outlineVariant}`,
                background: selected === g.id ? Z.primaryFixed : Z.surfaceContainerLowest,
                cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
                display: "flex", alignItems: "center", gap: 14,
                transition: "all 0.15s",
              }}
            >
              <div style={{ width: 46, height: 46, borderRadius: 14, background: selected === g.id ? Z.primaryFixedDim : Z.surfaceContainer, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                {g.icon}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: Z.onSurface, marginBottom: 2 }}>{g.label}</div>
                <div style={{ fontSize: 13, color: Z.onSurfaceVariant, lineHeight: 1.4 }}>{g.desc}</div>
              </div>
              {selected === g.id && <span style={{ marginLeft: "auto", fontSize: 18, flexShrink: 0 }}>✓</span>}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: "16px 20px", flexShrink: 0 }}>
        <button
          onClick={() => selected && onNext(GOALS.find((g) => g.id === selected))}
          style={{
            width: "100%", padding: "15px", borderRadius: 14, border: "none",
            background: selected ? Z.primary : Z.outlineVariant,
            color: selected ? Z.onPrimary : Z.outline,
            fontSize: 15, fontWeight: 600, cursor: selected ? "pointer" : "default",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: "background 0.2s",
          }}
        >
          Weiter →
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SCREEN 2 — PROTOKOLL WÄHLEN
// ─────────────────────────────────────────────
function ProtocolScreen({ goal, onNext, onBack }) {
  const [selected, setSelected] = useState(null);

  // Empfohlene Protokolle basierend auf Ziel + alle Vorbilder
  const recommended = goal.routines
    .map((id) => ROUTINES[id])
    .filter(Boolean);

  const others = getRoutinesByCategory("rolemodel")
    .filter((r) => !goal.routines.includes(r.id))
    .slice(0, 4);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 20px" }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 28, fontWeight: 300, color: Z.primary, letterSpacing: "-0.03em", lineHeight: 1.2, margin: "0 0 8px" }}>
            Wähle dein Vorbild
          </h1>
          <p style={{ fontSize: 14, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Passend zu deinem Ziel: {goal.icon} {goal.label}
          </p>
        </div>

        {/* Empfohlen */}
        <div style={{ fontSize: 11, fontWeight: 700, color: Z.primary, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Empfohlen
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {recommended.map((r) => (
            <ProtocolCard key={r.id} routine={r} selected={selected === r.id} onSelect={() => setSelected(r.id)} />
          ))}
        </div>

        {/* Andere */}
        {others.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: Z.outline, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Weitere Vorbilder
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 20 }}>
              {others.map((r) => (
                <ProtocolCard key={r.id} routine={r} selected={selected === r.id} onSelect={() => setSelected(r.id)} compact />
              ))}
            </div>
          </>
        )}

        {/* Basis-Protokoll Option */}
        <button
          onClick={() => setSelected("neuro")}
          style={{ width: "100%", padding: "12px 16px", borderRadius: 14, border: `1px dashed ${Z.outlineVariant}`, background: "transparent", color: Z.onSurfaceVariant, fontSize: 13, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 20 }}
        >
          Lieber ohne Vorbild starten → Neuro-Optimizer
        </button>
      </div>

      <div style={{ padding: "16px 20px", display: "flex", gap: 10, flexShrink: 0 }}>
        <button onClick={onBack} style={{ padding: "15px 20px", borderRadius: 14, border: `1px solid ${Z.outlineVariant}`, background: "transparent", color: Z.onSurfaceVariant, fontSize: 14, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          ←
        </button>
        <button
          onClick={() => selected && onNext(selected)}
          style={{ flex: 1, padding: "15px", borderRadius: 14, border: "none", background: selected ? Z.primary : Z.outlineVariant, color: selected ? Z.onPrimary : Z.outline, fontSize: 15, fontWeight: 600, cursor: selected ? "pointer" : "default", fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "background 0.2s" }}
        >
          Weiter →
        </button>
      </div>
    </div>
  );
}

function ProtocolCard({ routine, selected, onSelect, compact }) {
  return (
    <button
      onClick={onSelect}
      style={{
        textAlign: "left", padding: compact ? "12px 14px" : "14px 16px", borderRadius: 16,
        border: `${selected ? "2px" : "1px"} solid ${selected ? Z.primary : Z.outlineVariant}`,
        background: selected ? Z.primaryFixed : Z.surfaceContainerLowest,
        cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "all 0.15s",
        display: "flex", alignItems: "flex-start", gap: 12,
      }}
    >
      <div style={{ width: 38, height: 38, borderRadius: 10, background: selected ? Z.primaryFixedDim : Z.surfaceContainer, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {routine.icon || "🌿"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: Z.onSurface }}>
          {routine.name.includes("—") ? routine.name.split("—")[1].trim() : routine.name}
        </div>
        {routine.focus && <div style={{ fontSize: 11, color: Z.primary, fontWeight: 600, marginTop: 2 }}>{routine.focus}</div>}
        {!compact && <div style={{ fontSize: 12, color: Z.onSurfaceVariant, marginTop: 4, lineHeight: 1.4 }}>{routine.description}</div>}
        {!compact && routine.quote && (
          <div style={{ marginTop: 8, fontSize: 12, color: Z.onSurfaceVariant, fontStyle: "italic", borderLeft: `2px solid ${Z.primaryFixedDim}`, paddingLeft: 8 }}>
            „{routine.quote.slice(0, 80)}…"
          </div>
        )}
      </div>
      {selected && <span style={{ fontSize: 16, flexShrink: 0 }}>✓</span>}
    </button>
  );
}

// ─────────────────────────────────────────────
// SCREEN 3 — BEREIT
// ─────────────────────────────────────────────
function ReadyScreen({ routineId, onFinish }) {
  const routine = ROUTINES[routineId] || ROUTINES.neuro;
  const morningCount = routine.morning?.length || 0;
  const nightCount   = routine.night?.length   || 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", alignItems: "center", justifyContent: "center", padding: "0 28px", textAlign: "center" }}>

      <div style={{ width: 80, height: 80, borderRadius: 24, background: Z.primary, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, marginBottom: 24, boxShadow: "0 8px 32px rgba(85,98,77,0.25)" }}>
        {routine.icon || "🌿"}
      </div>

      <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 30, fontWeight: 300, color: Z.primary, letterSpacing: "-0.03em", margin: "0 0 12px", lineHeight: 1.2 }}>
        Alles bereit.
      </h1>

      <p style={{ fontSize: 15, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.7, marginBottom: 32 }}>
        Dein Protokoll ist aktiviert:<br />
        <strong style={{ color: Z.onSurface }}>
          {routine.name.includes("—") ? routine.name.split("—")[1].trim() : routine.name}
        </strong>
        <br />
        {morningCount} Morning-Habits · {nightCount} Night-Habits
      </p>

      {routine.quote && (
        <div style={{ padding: "14px 18px", background: Z.secondaryContainer, borderRadius: 16, marginBottom: 32, borderLeft: `3px solid ${Z.secondary}` }}>
          <span style={{ fontSize: 13, color: Z.onSecondaryContainer, fontStyle: "italic", lineHeight: 1.6, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            „{routine.quote}"
          </span>
        </div>
      )}

      <button
        onClick={onFinish}
        style={{ width: "100%", padding: "16px", borderRadius: 16, border: "none", background: Z.primary, color: Z.onPrimary, fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.01em" }}
      >
        Jetzt starten 🚀
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// ONBOARDING FLOW (Haupt-Export)
// ─────────────────────────────────────────────
export function OnboardingFlow({ onComplete }) {
  const [step, setStep]         = useState(0);
  const [goal, setGoal]         = useState(null);
  const [routineId, setRoutineId] = useState(null);

  const handleGoalNext = (selectedGoal) => {
    setGoal(selectedGoal);
    setStep(1);
  };

  const handleProtocolNext = (selectedRoutineId) => {
    setRoutineId(selectedRoutineId);
    setStep(2);
  };

  const handleFinish = () => {
    localStorage.setItem("habitOS_onboarded", "true");
    onComplete(routineId);
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; }
        button:active { transform: scale(0.98); }
      `}</style>

      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        background: `linear-gradient(160deg, ${Z.surface} 0%, ${Z.surfaceContainerLow} 60%, ${Z.secondaryContainer}44 100%)`,
      }}>

        {/* Header */}
        <div style={{ padding: "20px 20px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: 18, fontWeight: 500, color: Z.primary, letterSpacing: "-0.02em" }}>Habit OS</span>
          <ProgressDots total={3} current={step} />
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} style={{ border: "none", background: "none", fontSize: 13, color: Z.outline, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Zurück
            </button>
          ) : (
            <button onClick={() => { localStorage.setItem("habitOS_onboarded", "true"); onComplete("neuro"); }} style={{ border: "none", background: "none", fontSize: 13, color: Z.outline, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Überspringen
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {step === 0 && <GoalScreen onNext={handleGoalNext} />}
          {step === 1 && <ProtocolScreen goal={goal} onNext={handleProtocolNext} onBack={() => setStep(0)} />}
          {step === 2 && <ReadyScreen routineId={routineId} onFinish={handleFinish} />}
        </div>
      </div>
    </>
  );
}